import React, { useState, useEffect } from "react";
import { Calendar, Paperclip, MoreVertical, Pencil, Trash2, CheckCircle, RotateCcw, Folder, Inbox as InboxIcon, ArrowRightCircle } from "lucide-react";
import type { Task, TaskPriority } from "../../utils/taskHelpers";
import { getPriorityBadgeStyles, getStatusLabel, getAssigneeGradient, getUserInitials } from "../../utils/taskHelpers";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

// Helper per formattare la data da "2026-02-18" a "18/02/2026"
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "—";
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
};

// Helper per verificare se un task è scaduto
const isOverdue = (task: Task): boolean => {
  if (!task.due_date) return false;
  if (task.is_completed) return false;
  if (task.status?.key === "COMPLETATO" || task.status?.key === "ANNULLATO") return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(task.due_date);
  due.setHours(0, 0, 0, 0);
  return due < today;
};

interface TaskListViewProps {
  tasks: Task[];
  onOpenTask: (taskId: number) => void;
  onDeleteTask?: (taskId: number) => Promise<void>;
  onToggleCompleted?: (taskId: number) => Promise<void> | void;
  highlightedTaskId?: number | null;
  /**
   * Mostra la colonna "Progetto" (default true). Nella vista progetto si può
   * nascondere per ridurre clutter; nella vista inbox è utile mostrarla.
   */
  showProjectColumn?: boolean;
  /**
   * Callback per "Assegna a progetto/colonna" su un task inbox.
   * Se assente, la CTA non viene mostrata.
   */
  onAssignToProject?: (taskId: number) => void;
  /**
   * FE-5 §8: callback per "Sposta nella colonna..." (alternativa accessibile al drag).
   * Se assente, la voce non viene mostrata.
   */
  onMoveTaskToColumn?: (taskId: number) => void;
  /**
   * FE-5 §8: callback per "Sposta in un altro progetto".
   * Se assente, la voce non viene mostrata.
   */
  onMoveTaskToProject?: (taskId: number) => void;
}

export default function TaskListView({
  tasks,
  onOpenTask,
  onDeleteTask,
  onToggleCompleted,
  highlightedTaskId,
  showProjectColumn = true,
  onAssignToProject,
  onMoveTaskToColumn,
  onMoveTaskToProject,
}: TaskListViewProps) {
  // Task completati in fondo (stable sort)
  const displayTasks = [...tasks].sort(
    (a, b) => Number(!!a.is_completed) - Number(!!b.is_completed)
  );

  // Stato per gestire quale riga ha il menu aperto
  const [openMenuTaskId, setOpenMenuTaskId] = useState<number | null>(null);

  // Stati per gestire eliminazione task
  const [deleteConfirmTaskId, setDeleteConfirmTaskId] = useState<number | null>(null);
  const [isDeletingTask, setIsDeletingTask] = useState(false);

  // Handler per eliminare una task
  const handleConfirmDeleteTask = async () => {
    if (deleteConfirmTaskId && onDeleteTask) {
      setIsDeletingTask(true);
      try {
        await onDeleteTask(deleteConfirmTaskId);
        setDeleteConfirmTaskId(null);
      } catch (error) {
        console.error("Errore eliminazione task:", error);
      } finally {
        setIsDeletingTask(false);
      }
    }
  };

  // Chiudi menu quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuTaskId) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-task-menu]')) {
          setOpenMenuTaskId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuTaskId]);

  // Scroll-into-view sul task evidenziato (es. dopo click su notifica)
  useEffect(() => {
    if (!highlightedTaskId) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-task-id="${highlightedTaskId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [highlightedTaskId, tasks]);

  return (
    <div className="task-list rounded-2xl bg-[var(--hexa-sidebar-item-active)] backdrop-blur border border-[var(--hexa-border-subtle)]">
      {/* Header */}
      <div className="task-list-heading px-4 py-3 border-b border-[var(--hexa-border-subtle)]">
        <div
          className={`grid items-center gap-3 text-xs font-medium text-[var(--hexa-text-muted)] ${GRID_TEMPLATE_CLASS(showProjectColumn)}`}
        >
          <div>Titolo</div>
          {showProjectColumn && <div className="text-right pr-2">Progetto</div>}
          <div className="text-right pr-2">Colonna</div>
          <div className="text-right pr-2">Categoria</div>
          <div className="text-right pr-2">Priorità</div>
          <div className="text-right pr-2">Assegnato a</div>
          <div className="text-right pr-2">Scadenza</div>
          <div className="text-right pr-2">Allegati</div>
          <div className="text-right pr-2">Azioni</div>
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-[rgba(106,77,255,0.25)] dark:divide-[var(--hexa-border-subtle)]">
        {displayTasks.map((t) => {
          const taskIsOverdue = isOverdue(t);
          const isCompleted = !!t.is_completed;
          const isInbox = t.project == null;
          return (
            <div
            key={t.id}
            data-task-id={t.id}
            className={`task-list-row relative px-4 py-3 hover:bg-[var(--hexa-sidebar-item-hover)] transition cursor-pointer ${
              isCompleted
                ? "border-l-4 border-l-[rgba(255,255,255,0.2)] opacity-40"
                : taskIsOverdue
                  ? "border-l-4 border-l-[#ef4444] bg-[rgba(239,68,68,0.05)]"
                  : ""
            } ${highlightedTaskId === t.id ? "task-highlight" : ""}`}
            onClick={() => onOpenTask(t.id)}
          >
            <div className={`grid items-center gap-3 ${GRID_TEMPLATE_CLASS(showProjectColumn)}`}>
              {/* Titolo */}
              <div data-field="title" className="min-w-0 break-words text-sm text-[var(--hexa-text-primary)] flex flex-wrap items-center gap-y-1">
                <span className={isCompleted ? "line-through" : ""}>
                  {t.title}
                </span>
                {isCompleted && (
                  <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full"
                    style={{
                      background: 'rgba(123, 63, 255, 0.15)',
                      color: '#7b3fff',
                    }}>
                    Completato
                  </span>
                )}
                {isInbox && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full"
                    style={{
                      background: "rgba(107,114,128,0.15)",
                      color: "var(--hexa-text-secondary)",
                    }}
                    title="Task senza progetto"
                  >
                    <InboxIcon className="h-3 w-3" />
                    Inbox
                  </span>
                )}
              </div>

              {/* Progetto (solo se richiesto) */}
              {showProjectColumn && (
                <div data-field="project" data-label="Progetto" className="text-right pr-2">
                  <ProjectCell task={t} />
                </div>
              )}

              {/* Colonna (Status) — tollera null per inbox */}
              <div data-field="status" data-label="Colonna" className="text-right pr-2">
                <StatusText status={t.status} />
              </div>

              {/* Categoria */}
              <div data-field="category" data-label="Categoria" className="text-sm text-[var(--hexa-text-secondary)] truncate text-right pr-2">
                {t.category || "—"}
              </div>

              {/* Priorità */}
              <div data-field="priority" className="text-right pr-2">
                <PriorityBadge priority={t.priority} />
              </div>

              {/* Assegnato a */}
              <div data-field="assignee" data-label="Assegnato a" className="flex justify-end pr-2">
                {t.assigned_to && t.assigned_to.length > 0 ? (
                  <AssigneeChip assignees={t.assigned_to} />
                ) : (
                  <span className="text-xs text-[var(--hexa-text-muted)]">—</span>
                )}
              </div>

              {/* Scadenza */}
              <div data-field="due" className="flex items-center justify-end pr-2">
                {taskIsOverdue ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    style={{
                      background: "rgba(239,68,68,0.15)",
                      border: "1px solid rgba(239,68,68,0.3)",
                      color: "#ef4444",
                    }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full bg-[#ef4444]"
                      style={{ animation: "pulse 1.5s ease-in-out infinite" }}
                    />
                    {formatDate(t.due_date)} · Scaduto
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-xs text-[var(--hexa-text-secondary)]">
                    <Calendar className="h-4 w-4 text-[var(--hexa-text-secondary)]" />
                    {formatDate(t.due_date)}
                  </span>
                )}
              </div>

              {/* Allegati */}
              <div data-field="attachments" data-label="Allegati" className="flex items-center justify-end gap-2 pr-2 text-xs text-[var(--hexa-text-secondary)]">
                <Paperclip className="h-4 w-4 text-[var(--hexa-text-secondary)]" />
                <span>{t.attachments?.length || 0}</span>
              </div>

              {/* Azioni */}
              <div data-field="actions" className="flex justify-end pr-2">
                <div className="relative" data-task-menu>
                  <button
                    type="button"
                    className="h-8 w-8 rounded-lg grid place-items-center text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)] transition"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuTaskId(openMenuTaskId === t.id ? null : t.id);
                    }}
                    aria-label="Azioni task"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>

                  {/* Dropdown menu */}
                  {openMenuTaskId === t.id && (
                    <div
                      className="absolute right-0 top-full mt-1 w-40 bg-[var(--hexa-surface)] border border-[var(--hexa-border)] rounded-[10px] shadow-lg z-50 overflow-hidden"
                      style={{
                        animation: "menuFadeIn 150ms ease-out forwards",
                      }}
                    >
                      <style>{`
                        @keyframes menuFadeIn {
                          from {
                            opacity: 0;
                            transform: translateY(-4px);
                          }
                          to {
                            opacity: 1;
                            transform: translateY(0);
                          }
                        }
                      `}</style>

                      {/* Modifica */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuTaskId(null);
                          onOpenTask(t.id);
                        }}
                        className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)] transition"
                      >
                        <Pencil className="h-4 w-4" />
                        Modifica
                      </button>

                      {/* Assegna a progetto/colonna (solo inbox) */}
                      {isInbox && onAssignToProject && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuTaskId(null);
                            onAssignToProject(t.id);
                          }}
                          className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[var(--hexa-purple)] hover:bg-[var(--hexa-surface-hover)] transition"
                        >
                          <ArrowRightCircle className="h-4 w-4" />
                          Assegna a progetto
                        </button>
                      )}

                      {/* FE-5 §8: Sposta nella colonna... (solo task di progetto, non inbox) */}
                      {!isInbox && onMoveTaskToColumn && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuTaskId(null);
                            onMoveTaskToColumn(t.id);
                          }}
                          className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[var(--hexa-purple)] hover:bg-[var(--hexa-surface-hover)] transition"
                        >
                          <ArrowRightCircle className="h-4 w-4" />
                          Sposta nella colonna...
                        </button>
                      )}

                      {/* FE-5 §8: Sposta in un altro progetto (solo task di progetto, non inbox) */}
                      {!isInbox && onMoveTaskToProject && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuTaskId(null);
                            onMoveTaskToProject(t.id);
                          }}
                          className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[var(--hexa-purple)] hover:bg-[var(--hexa-surface-hover)] transition"
                        >
                          <Folder className="h-4 w-4" />
                          Sposta in un altro progetto
                        </button>
                      )}

                      {/* Toggle completato */}
                      {onToggleCompleted && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuTaskId(null);
                            onToggleCompleted(t.id);
                          }}
                          className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)] transition"
                        >
                          {isCompleted ? (
                            <>
                              <RotateCcw className="h-4 w-4" />
                              Riprendi
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4" />
                              Completa
                            </>
                          )}
                        </button>
                      )}

                      {/* Elimina */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuTaskId(null);
                          setDeleteConfirmTaskId(t.id);
                        }}
                        className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[#ef4444] hover:bg-[var(--hexa-surface-hover)] transition"
                      >
                        <Trash2 className="h-4 w-4" />
                        Elimina
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
        })}
      </div>

      {/* AlertDialog per conferma eliminazione task */}
      <AlertDialog open={deleteConfirmTaskId !== null} onOpenChange={(open) => !open && setDeleteConfirmTaskId(null)}>
        <AlertDialogContent className="bg-[var(--hexa-surface)] border-[var(--hexa-border)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--hexa-text-primary)]">Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--hexa-text-secondary)]">
              Sei sicuro di voler eliminare questa task? Questa azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isDeletingTask}
              className="bg-[var(--hexa-surface-elevated)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteTask}
              disabled={isDeletingTask}
              className="bg-[#ef4444] text-white hover:bg-[#dc2626]"
            >
              {isDeletingTask ? "Eliminazione..." : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* -----------------------------
   Sub-components
------------------------------ */

/**
 * Grid template dinamico: include la colonna Progetto solo se richiesto.
 * Mantiene allineamento header/row.
 */
function GRID_TEMPLATE_CLASS(showProject: boolean): string {
  return showProject
    ? "grid-cols-[1.3fr_110px_100px_100px_90px_130px_100px_70px_60px]"
    : "grid-cols-[1.3fr_100px_100px_90px_130px_100px_70px_60px]";
}

/**
 * Cell che mostra il progetto del task (Folder colorato) o "Inbox" se senza progetto.
 */
function ProjectCell({ task }: { task: Task }) {
  if (task.project == null) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-[var(--hexa-text-muted)]">
        <InboxIcon className="h-3.5 w-3.5" />
        <span className="truncate max-w-[80px]">Inbox</span>
      </span>
    );
  }
  const pd = task.project_detail;
  const color = pd?.color || "var(--hexa-purple)";
  const name = pd?.name || `#${task.project}`;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-[var(--hexa-text-secondary)]">
      <span
        className="inline-flex items-center justify-center w-4 h-4 rounded"
        style={{ backgroundColor: `${color}22`, color }}
      >
        <Folder className="h-3 w-3" />
      </span>
      <span className="truncate max-w-[80px]" title={name}>
        {name}
      </span>
    </span>
  );
}

function StatusText({ status }: { status: any }) {
  const getStatusColor = (key: string) => {
    const colorMap: Record<string, string> = {
      DA_FARE: "text-[#2b7fff]",
      IN_CORSO: "text-[#2b7fff]",
      COMPLETATO: "text-[#00d492]",
      REVISIONE: "text-[#fe9a00]",
    };
    return colorMap[key] || "text-[var(--hexa-text-secondary)]";
  };

  if (!status || !status.key) {
    return <span className="text-xs font-medium text-[var(--hexa-text-secondary)]">—</span>;
  }

  return (
    <span className={`text-xs font-medium ${getStatusColor(status.key)}`}>
      {getStatusLabel(status)}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: TaskPriority }) {
  const styles = getPriorityBadgeStyles(priority);

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${styles.bg} ${styles.text} ${styles.border}`}>
      {styles.label.toLowerCase()}
    </span>
  );
}

function AssigneeChip({ assignees }: { assignees: any[] }) {
  const firstAssignee = assignees[0];
  const hasMore = assignees.length > 1;

  return (
    <div className="flex items-center gap-2">
      <div
        className={`h-5 w-5 rounded-full ${getAssigneeGradient(firstAssignee.id)} grid place-items-center`}
        title={firstAssignee.username}
      >
        <span className="text-[9px] font-semibold text-white">
          {getUserInitials(firstAssignee.username)}
        </span>
      </div>
      <span className="text-xs text-[var(--hexa-text-primary)]">
        {firstAssignee.username}
        {hasMore && ` +${assignees.length - 1}`}
      </span>
    </div>
  );
}

